import React, { useState, useEffect, useCallback, useRef, useMemo, useImperativeHandle, forwardRef } from "react";
import { FileText, BookOpen, Briefcase, User, ChevronRight, ChevronLeft, Search } from "lucide-react";
import { caiApi } from "../api";

// ─── In-memory cache (24h TTL, shared across re-renders) ───

const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache: Record<string, CacheEntry<ContextItem[]>> = {};

async function cachedFetch(key: string, fetcher: () => Promise<ContextItem[]>): Promise<ContextItem[]> {
  const entry = cache[key];
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
    return entry.data;
  }
  const data = await fetcher();
  cache[key] = { data, timestamp: Date.now() };
  return data;
}

// ─── Types ───

export interface ContextItem {
  id: string;
  displayName: string;
  metadata: Record<string, unknown>;
}

interface ContextCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  fetchItems: () => Promise<ContextItem[]>;
}

export interface ContextMenuHandle {
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

interface ContextMenuProps {
  query: string;
  onSelect: (item: ContextItem) => void;
  onClose: () => void;
  uploadedFiles?: string[];
}

const CATEGORIES: ContextCategory[] = [
  {
    id: "files",
    label: "Files",
    icon: <FileText size={14} />,
    fetchItems: async () => [],
  },
  {
    id: "knowledgeBases",
    label: "Knowledge Bases",
    icon: <BookOpen size={14} />,
    fetchItems: () =>
      cachedFetch("knowledgeBases", async () => {
        const kbs = await caiApi.fetchUserKnowledgeBases();
        return kbs.map((kb) => ({
          id: kb.agent_name,
          displayName: kb.agent_name,
          metadata: { type: "knowledge_base", agent_name: kb.agent_name, agent_description: kb.agent_description },
        }));
      }),
  },
  {
    id: "managers",
    label: "Managers",
    icon: <Briefcase size={14} />,
    fetchItems: () =>
      cachedFetch("managers", async () => {
        const managers = await caiApi.fetchAllManagers();
        return managers.map((m) => ({
          id: m.productId,
          displayName: m.productShortName,
          metadata: { type: "manager", productId: m.productId, productShortName: m.productShortName },
        }));
      }),
  },
  {
    id: "people",
    label: "People",
    icon: <User size={14} />,
    fetchItems: () =>
      cachedFetch("people", async () => {
        const people = await caiApi.fetchAllPersons();
        return people.map((p) => ({
          id: p.email,
          displayName: p.display_name,
          metadata: { type: "person", email: p.email, display_name: p.display_name },
        }));
      }),
  },
];

export const ContextMenu = forwardRef<ContextMenuHandle, ContextMenuProps>(
  function ContextMenu({ query, onSelect, onClose, uploadedFiles = [] }, ref) {
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [items, setItems] = useState<ContextItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(0);
    const [search, setSearch] = useState("");
    const listRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    // File items from uploaded files
    const fileItems: ContextItem[] = useMemo(
      () =>
        uploadedFiles.map((name) => ({
          id: name,
          displayName: name,
          metadata: { type: "file", filename: name },
        })),
      [uploadedFiles]
    );

    // Load items when entering a category
    useEffect(() => {
      if (!activeCategory) return;

      if (activeCategory === "files") {
        setItems(fileItems);
        setLoading(false);
        return;
      }

      const category = CATEGORIES.find((c) => c.id === activeCategory);
      if (!category) return;

      setLoading(true);
      category
        .fetchItems()
        .then((result) => {
          setItems(result);
          setHighlightIndex(0);
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, [activeCategory, fileItems]);

    // Filter items by search or initial query
    const filterText = activeCategory ? search : query;
    const filteredItems = useMemo(() => {
      if (!filterText) return items;
      const lower = filterText.toLowerCase();
      return items.filter((item) =>
        item.displayName.toLowerCase().includes(lower)
      );
    }, [items, filterText]);

    // Filter categories by query when at top level
    const filteredCategories = useMemo(() => {
      if (!query) return CATEGORIES;
      const lower = query.toLowerCase();
      return CATEGORIES.filter((c) => c.label.toLowerCase().includes(lower));
    }, [query]);

    // Reset highlight when list changes
    useEffect(() => {
      setHighlightIndex(0);
    }, [filteredItems.length, filteredCategories.length, activeCategory]);

    // Focus search input when entering category
    useEffect(() => {
      if (activeCategory) {
        setTimeout(() => searchRef.current?.focus(), 50);
      }
    }, [activeCategory]);

    // Keyboard navigation — called from ChatInput via ref, or from search input
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        const list = activeCategory ? filteredItems : filteredCategories;
        const maxIndex = list.length - 1;

        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlightIndex((i) => Math.min(i + 1, maxIndex));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setHighlightIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          if (activeCategory) {
            if (filteredItems[highlightIndex]) {
              onSelect(filteredItems[highlightIndex]);
            }
          } else {
            const cat = filteredCategories[highlightIndex] as ContextCategory | undefined;
            if (cat) setActiveCategory(cat.id);
          }
        } else if (e.key === "Escape") {
          e.preventDefault();
          if (activeCategory) {
            setActiveCategory(null);
            setSearch("");
          } else {
            onClose();
          }
        } else if (e.key === "Backspace" && activeCategory && search === "") {
          e.preventDefault();
          setActiveCategory(null);
          setSearch("");
        }
      },
      [activeCategory, filteredItems, filteredCategories, highlightIndex, search, onSelect, onClose]
    );

    // Expose handleKeyDown to parent via ref
    useImperativeHandle(ref, () => ({ handleKeyDown }), [handleKeyDown]);

    // Scroll highlighted item into view
    useEffect(() => {
      const el = listRef.current?.children[highlightIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: "nearest" });
    }, [highlightIndex]);

    const categoryIcon = (catId: string) => {
      const cat = CATEGORIES.find((c) => c.id === catId);
      return cat?.icon ?? null;
    };

    return (
      <div className="absolute bottom-full left-0 right-0 mx-3 mb-1 z-30 cai-fade-up">
        <div className="bg-surface border border-border-subtle rounded-xl shadow-lg overflow-hidden max-h-[280px] flex flex-col backdrop-blur-xl">
          {/* Header when inside a category */}
          {activeCategory && (
            <button
              onClick={() => { setActiveCategory(null); setSearch(""); }}
              className="flex items-center gap-2 px-3 py-2 text-[11px] text-text-secondary hover:bg-surface-glass transition-colors border-b border-border-subtle"
            >
              <ChevronLeft size={12} />
              <span>{CATEGORIES.find((c) => c.id === activeCategory)?.label}</span>
            </button>
          )}

          {/* Search within category */}
          {activeCategory && (
            <div className="px-3 py-2 border-b border-border-subtle">
              <div className="flex items-center gap-2 bg-surface-glass text-text-secondary rounded-lg px-2.5 py-1.5">
                <Search size={11} className="shrink-0 text-text-muted" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search..."
                  className="flex-1 bg-transparent text-[11px] text-text-primary placeholder:text-text-muted focus:outline-none"
                />
              </div>
            </div>
          )}

          {/* List */}
          <div ref={listRef} className="overflow-y-auto flex-1" style={{ maxHeight: 210 }}>
            {!activeCategory ? (
              // Category list
              filteredCategories.map((cat, i) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  onMouseEnter={() => setHighlightIndex(i)}
                  className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors ${
                    i === highlightIndex
                      ? "bg-accent-violet/10 text-text-primary"
                      : "text-text-secondary hover:bg-surface-glass"
                  }`}
                >
                  <span className="text-text-muted shrink-0">{cat.icon}</span>
                  <span className="flex-1 text-[12px]">{cat.label}</span>
                  <ChevronRight size={12} className="text-text-muted shrink-0" />
                </button>
              ))
            ) : loading ? (
              <div className="px-3.5 py-4 text-center text-[11px] text-text-muted">Loading...</div>
            ) : filteredItems.length === 0 ? (
              <div className="px-3.5 py-4 text-center text-[11px] text-text-muted">
                {activeCategory === "files" && fileItems.length === 0
                  ? "No files uploaded in this session"
                  : "No results found"}
              </div>
            ) : (
              // Item list
              filteredItems.map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => onSelect(item)}
                  onMouseEnter={() => setHighlightIndex(i)}
                  className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-left transition-colors ${
                    i === highlightIndex
                      ? "bg-accent-violet/10 text-text-primary"
                      : "text-text-secondary hover:bg-surface-glass"
                  }`}
                >
                  <span className="text-text-muted shrink-0">
                    {categoryIcon(activeCategory)}
                  </span>
                  <span className="flex-1 text-[12px] truncate" title={item.displayName}>
                    {item.displayName}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }
);
